/**
 * Auction experience: countdown, bid form validation/state (backed by the
 * Cloudflare Worker in workers/auction-bidding), and client-side sort of
 * the live auctions grid.
 */

function formatRelativeTime(isoString) {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMinutes = Math.round(diffMs / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} min ago`;
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;
  const diffDays = Math.round(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

document.querySelectorAll('[data-bid-at]').forEach((el) => {
  el.textContent = formatRelativeTime(el.dataset.bidAt);
});

function findBidFormFor(productGid) {
  return document.querySelector(`auction-bid-form[data-product-gid="${productGid}"]`);
}

function syncBidFormDisplays(productGid, result) {
  const bidForm = findBidFormFor(productGid);
  if (bidForm && typeof bidForm.syncFromExternalChange === 'function') {
    bidForm.syncFromExternalChange(result);
  }
}

/** Wires the Edit/Delete controls on one bid-history row (server-rendered or freshly prepended). */
function wireBidHistoryRow(row, panel) {
  const editButton = row.querySelector('[data-bid-edit]');
  const deleteButton = row.querySelector('[data-bid-delete]');
  if (!editButton && !deleteButton) return;

  const productGid = panel.dataset.productGid;
  const endpoint = panel.dataset.bidEndpoint;
  const bidId = row.dataset.bidId;
  const amountEl = row.querySelector('.auction-bid-history__amount');

  async function postJSON(path, body) {
    const response = await fetch(`${endpoint}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    return { ok: response.ok, data };
  }

  function showRowError(message) {
    let errorEl = row.querySelector('.auction-bid-history__row-error');
    if (!errorEl) {
      errorEl = document.createElement('div');
      errorEl.className = 'auction-bid-history__row-error';
      row.appendChild(errorEl);
    }
    errorEl.textContent = message;
  }

  function enterEditMode() {
    if (row.querySelector('.auction-bid-history__edit-form')) return;
    const currentAmount = row.dataset.bidAmount;
    const form = document.createElement('div');
    form.className = 'auction-bid-history__edit-form';
    form.innerHTML = `
      <input type="number" class="field__input" value="${currentAmount}" data-edit-input>
      <button type="button" class="auction-bid-history__action" data-edit-save>Save</button>
      <button type="button" class="auction-bid-history__action" data-edit-cancel>Cancel</button>
    `;
    row.appendChild(form);
    row.classList.add('is-editing');

    form.querySelector('[data-edit-cancel]').addEventListener('click', () => {
      form.remove();
      row.classList.remove('is-editing');
    });

    form.querySelector('[data-edit-save]').addEventListener('click', async () => {
      const input = form.querySelector('[data-edit-input]');
      const newAmount = parseFloat(input.value);
      if (Number.isNaN(newAmount) || newAmount <= 0) {
        showRowError('Enter a valid amount.');
        return;
      }
      const { ok, data } = await postJSON('/bid/edit', { productId: productGid, bidId, amount: newAmount });
      if (!ok) {
        showRowError(data.error || 'Could not update your bid.');
        return;
      }
      row.dataset.bidAmount = String(newAmount);
      if (amountEl) amountEl.textContent = `₹ ${newAmount.toLocaleString('en-IN')}`;
      form.remove();
      row.classList.remove('is-editing');
      syncBidFormDisplays(productGid, data);
    });
  }

  async function handleDelete() {
    if (!window.confirm('Withdraw this bid?')) return;
    const { ok, data } = await postJSON('/bid/delete', { productId: productGid, bidId });
    if (!ok) {
      showRowError(data.error || 'Could not withdraw your bid.');
      return;
    }
    row.remove();
    const list = panel.querySelector('[data-bid-history-list]');
    const emptyState = panel.querySelector('[data-auction-empty-state]');
    const countDisplay = panel.querySelector('[data-bid-count-display]');
    if (list && list.children.length === 0) {
      list.hidden = true;
      if (emptyState) emptyState.hidden = false;
    }
    if (countDisplay) countDisplay.textContent = `${data.bidCount} Bids`;
    syncBidFormDisplays(productGid, data);
  }

  if (editButton) editButton.addEventListener('click', enterEditMode);
  if (deleteButton) deleteButton.addEventListener('click', handleDelete);
}

document.querySelectorAll('[data-auction-bid-history]').forEach((panel) => {
  panel.querySelectorAll('[data-bid-id]').forEach((row) => wireBidHistoryRow(row, panel));
});

class AuctionCountdown extends HTMLElement {
  connectedCallback() {
    this.endTime = this.dataset.end ? new Date(this.dataset.end).getTime() : null;
    this.startTime = this.dataset.start ? new Date(this.dataset.start).getTime() : null;
    this.phase = this.dataset.status === 'upcoming' ? 'upcoming' : 'live';

    if (this.dataset.status === 'ended' || !this.endTime) return;

    this.labelEl = this.querySelector('[data-countdown-label]');
    this.daysEl = this.querySelector('[data-days]');
    this.hoursEl = this.querySelector('[data-hours]');
    this.minutesEl = this.querySelector('[data-minutes]');
    this.secondsEl = this.querySelector('[data-seconds]');

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.interval);
  }

  tick() {
    const target = this.phase === 'upcoming' ? this.startTime : this.endTime;
    const diff = target - Date.now();

    if (diff <= 0) {
      if (this.phase === 'upcoming') {
        this.phase = 'live';
        if (this.labelEl) this.labelEl.textContent = 'Ends In';
        this.dispatchEvent(new CustomEvent('auction:phase-change', { bubbles: true, detail: { phase: 'live' } }));
        this.tick();
        return;
      }

      clearInterval(this.interval);
      this.dispatchEvent(new CustomEvent('auction:phase-change', { bubbles: true, detail: { phase: 'ended' } }));
      this.innerHTML = '<p class="auction-countdown__ended">Auction Ended</p>';
      return;
    }

    const pad = (n) => String(n).padStart(2, '0');
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (this.daysEl) this.daysEl.textContent = pad(days);
    if (this.hoursEl) this.hoursEl.textContent = pad(hours);
    if (this.minutesEl) this.minutesEl.textContent = pad(minutes);
    if (this.secondsEl) this.secondsEl.textContent = pad(seconds);
  }
}

class AuctionBidForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-bid-input]');
    this.decrementButton = this.querySelector('[data-bid-decrement]');
    this.incrementButton = this.querySelector('[data-bid-increment]');
    this.submitButton = this.querySelector('[data-bid-submit]');
    this.messageEl = this.querySelector('[data-bid-message]');

    this.currentBidDisplay = this.querySelector('[data-current-bid-display]');
    this.bidCountDisplay = this.querySelector('[data-bid-count-display]');
    this.minNextBidDisplay = this.querySelector('[data-min-next-bid-display]');

    this.currentBid = parseFloat(this.dataset.currentBid || '0');
    this.increment = parseFloat(this.dataset.increment || '1');
    this.minNextBid = this.currentBid + this.increment;
    this.productGid = this.dataset.productGid;
    this.endpoint = this.dataset.bidEndpoint;
    this.submitting = false;

    this.historyPanel = document.getElementById(`AuctionBidHistory-${this.productGid.split('/').pop()}`);

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));
    }
    if (this.decrementButton) {
      this.decrementButton.addEventListener('click', () => this.step(-1));
    }
    if (this.incrementButton) {
      this.incrementButton.addEventListener('click', () => this.step(1));
    }
    if (this.input) {
      this.input.addEventListener('input', () => this.clearMessage());
    }

    this.addEventListener('auction:phase-change', (event) => this.handlePhaseChange(event.detail.phase));
  }

  handlePhaseChange(phase) {
    if (phase === 'ended') {
      this.setDisabled(true, 'Auction Ended');
    } else if (phase === 'live') {
      this.setDisabled(false, 'Place a Bid');
    }
  }

  setDisabled(disabled, label) {
    if (!this.submitButton) return;
    this.submitButton.disabled = disabled;
    this.submitButton.textContent = label;
    if (this.input) this.input.disabled = disabled;
    if (this.decrementButton) this.decrementButton.disabled = disabled;
    if (this.incrementButton) this.incrementButton.disabled = disabled;
  }

  step(direction) {
    if (!this.input) return;
    const value = parseFloat(this.input.value || this.minNextBid);
    const next = value + direction * this.increment;
    this.input.value = Math.max(next, this.minNextBid);
    this.clearMessage();
  }

  clearMessage() {
    if (!this.messageEl) return;
    this.messageEl.textContent = '';
    this.messageEl.removeAttribute('data-state');
  }

  showMessage(text, state) {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.setAttribute('data-state', state);
  }

  validate(value) {
    if (this.dataset.status === 'ended') {
      return 'This auction has ended. Bidding is closed.';
    }
    if (this.dataset.status === 'upcoming') {
      return 'This auction has not started yet.';
    }
    if (Number.isNaN(value) || value <= 0) {
      return 'Enter a bid amount.';
    }
    if (value < this.minNextBid) {
      return `Your bid must be at least ₹ ${this.minNextBid.toLocaleString('en-IN')}.`;
    }
    const stepsAboveCurrent = (value - this.currentBid) / this.increment;
    if (Math.abs(stepsAboveCurrent - Math.round(stepsAboveCurrent)) > 0.001) {
      return `Bids must increase in steps of ₹ ${this.increment.toLocaleString('en-IN')}.`;
    }
    return null;
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;

    const value = parseFloat(this.input ? this.input.value : '');
    const error = this.validate(value);
    if (error) {
      this.showMessage(error, 'error');
      return;
    }

    this.submitting = true;
    if (this.submitButton) {
      this.submitButton.disabled = true;
      this.submitButton.classList.add('is-loading');
      this.submitButton.textContent = 'Submitting…';
    }
    this.clearMessage();

    this.submitBid(value)
      .then((result) => {
        if (result.success) {
          this.showMessage(result.message, 'success');
          this.applyAcceptedBid(result, value);
        } else {
          this.showMessage(result.error, 'error');
          if (typeof result.currentBid === 'number') {
            this.syncCurrentBid(result.currentBid, result.bidCount);
          }
        }
      })
      .catch(() => {
        this.showMessage('Couldn’t reach the bidding server. Check your connection and try again.', 'error');
      })
      .finally(() => {
        this.submitting = false;
        if (this.submitButton) {
          this.submitButton.disabled = false;
          this.submitButton.classList.remove('is-loading');
          this.submitButton.textContent = 'Place a Bid';
        }
      });
  }

  /**
   * Submits the bid to the auction bidding backend (a Cloudflare Worker --
   * see workers/auction-bidding in the repo root). The Worker re-validates
   * everything server-side (minimum bid, increment, auction open/closed)
   * and is the only thing with write access to Shopify -- this call can
   * fail or be rejected even when client-side validate() passed, e.g. if
   * someone else's bid landed a moment earlier.
   */
  async submitBid(amount) {
    const response = await fetch(`${this.endpoint}/bid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId: this.productGid, amount }),
    });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'Your bid was rejected.', currentBid: data.currentBid, bidCount: data.bidCount };
    }
    return { success: true, ...data };
  }

  applyAcceptedBid(result, amount) {
    this.currentBid = result.currentBid;
    this.minNextBid = result.minNextBid;
    if (this.currentBidDisplay) this.currentBidDisplay.textContent = `₹ ${result.currentBid.toLocaleString('en-IN')}`;
    if (this.bidCountDisplay) this.bidCountDisplay.textContent = String(result.bidCount);
    if (this.minNextBidDisplay) this.minNextBidDisplay.textContent = `₹ ${result.minNextBid.toLocaleString('en-IN')}`;
    if (this.input) {
      this.input.min = String(result.minNextBid);
      this.input.value = String(result.minNextBid);
    }
    if (result.bidId) {
      this.prependHistoryRow(result.bidId, amount);
    } else if (this.historyPanel) {
      window.location.reload();
    }
  }

  /** Applies a fresh current-bid/count/min-next-bid snapshot after an edit or delete elsewhere on the page. */
  syncFromExternalChange(result) {
    this.syncCurrentBid(result.currentBid, result.bidCount);
  }

  syncCurrentBid(currentBid, bidCount) {
    this.currentBid = currentBid;
    this.minNextBid = currentBid + this.increment;
    if (this.currentBidDisplay) this.currentBidDisplay.textContent = `₹ ${currentBid.toLocaleString('en-IN')}`;
    if (typeof bidCount === 'number' && this.bidCountDisplay) this.bidCountDisplay.textContent = String(bidCount);
    if (this.minNextBidDisplay) this.minNextBidDisplay.textContent = `₹ ${this.minNextBid.toLocaleString('en-IN')}`;
    if (this.input) {
      this.input.min = String(this.minNextBid);
      this.input.value = String(this.minNextBid);
    }
  }

  prependHistoryRow(bidId, amount) {
    if (!this.historyPanel) return;
    const list = this.historyPanel.querySelector('[data-bid-history-list]');
    const emptyState = this.historyPanel.querySelector('[data-auction-empty-state]');
    const countDisplay = this.historyPanel.querySelector('[data-bid-count-display]');
    if (!list) return;

    const bidderName = this.dataset.customerName || 'Guest Bidder';
    const parts = bidderName.trim().split(/\s+/);
    const initials = parts.length === 1
      ? `${(parts[0][0] || 'G').toUpperCase()}.`
      : `${(parts[0][0] || 'G').toUpperCase()}.${(parts[parts.length - 1][0] || '').toUpperCase()}.`;

    const row = document.createElement('li');
    row.className = 'auction-bid-history__row';
    row.dataset.bidId = bidId;
    row.dataset.bidCustomerGid = this.dataset.customerGid || '';
    row.dataset.bidAmount = String(amount);
    row.innerHTML = `
      <span class="auction-bid-history__avatar" aria-hidden="true">${initials}</span>
      <span class="auction-bid-history__amount">₹ ${amount.toLocaleString('en-IN')}</span>
      <span class="auction-bid-history__time">Just now</span>
      <span class="auction-bid-history__owner-actions">
        <button type="button" class="auction-bid-history__action" data-bid-edit>Edit</button>
        <button type="button" class="auction-bid-history__action auction-bid-history__action--delete" data-bid-delete>Delete</button>
      </span>
    `;
    list.prepend(row);
    list.hidden = false;
    if (emptyState) emptyState.hidden = true;
    if (countDisplay) countDisplay.textContent = `${list.children.length} Bids`;
    wireBidHistoryRow(row, this.historyPanel);
  }
}

class AuctionGallery extends HTMLElement {
  connectedCallback() {
    this.mainImage = this.querySelector('[data-auction-gallery-main]');
    this.thumbs = Array.from(this.querySelectorAll('[data-auction-gallery-thumb]'));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.select(thumb));
    });
  }

  select(thumb) {
    if (!this.mainImage) return;
    const full = thumb.dataset.full;
    if (full) this.mainImage.src = full;
    this.thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
  }
}

class AuctionSort extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.grid = document.querySelector(this.dataset.target);
    if (!this.select || !this.grid) return;
    this.select.addEventListener('change', () => this.sort());
  }

  sort() {
    const cards = Array.from(this.grid.querySelectorAll('[data-auction-card]'));
    const key = this.select.value;

    const comparators = {
      ending_soon: (a, b) => Number(a.dataset.endTime) - Number(b.dataset.endTime),
      recently_added: (a, b) => Number(b.dataset.createdAt) - Number(a.dataset.createdAt),
      highest_bid: (a, b) => Number(b.dataset.currentBid) - Number(a.dataset.currentBid),
      lowest_bid: (a, b) => Number(a.dataset.currentBid) - Number(b.dataset.currentBid),
    };

    const comparator = comparators[key];
    if (!comparator) return;

    cards.sort(comparator).forEach((card) => this.grid.appendChild(card));
  }
}

if (!customElements.get('auction-countdown')) customElements.define('auction-countdown', AuctionCountdown);
if (!customElements.get('auction-bid-form')) customElements.define('auction-bid-form', AuctionBidForm);
if (!customElements.get('auction-gallery')) customElements.define('auction-gallery', AuctionGallery);
if (!customElements.get('auction-sort')) customElements.define('auction-sort', AuctionSort);
